/**
 * 도면 견적(시범) 원가 계산 - 순수 함수만 모아둔다(화면 상태/axios 없음).
 * 그래야 cadQuoteCalc.test.js 처럼 이 파일만 따로, 빠르게 테스트할 수 있다.
 *
 * 금액은 중간 단계마다 반올림하지 않고 끝에서만(roundWon) 정수(원)로 맺는다 -
 * 중간에 자꾸 반올림하면 "소재비 + 가공비"와 "합계"가 1원씩 안 맞는 경우가 생긴다.
 *
 * 재질 기본값(MATERIALS)은 시범용 임시 단가다 - 화면에도 "임시 기본값, 실제
 * 단가로 수정하세요" 안내를 띄운다. 숫자 자체는 과제에서 받은 값 그대로다.
 */

// 재질 키: 소재비(density/pricePerKg)와 가공시간(roughRate/finishRate) 기본값.
// roughRate(cm3/분) = 황삭으로 1분에 깎아내는 부피, finishRate(cm2/분) = 정삭으로
// 1분에 다듬는 표면적. 둘 다 재질이 무를수록(AL계열) 커진다.
export const MATERIALS = {
  SKD11: { density: 7.7, pricePerKg: 9000, roughRate: 5, finishRate: 15 },
  SKD61: { density: 7.8, pricePerKg: 10000, roughRate: 5, finishRate: 15 },
  NAK80: { density: 7.8, pricePerKg: 12000, roughRate: 7, finishRate: 18 },
  STAVAX: { density: 7.8, pricePerKg: 15000, roughRate: 5, finishRate: 15 },
  S45C: { density: 7.85, pricePerKg: 3000, roughRate: 10, finishRate: 25 },
  SCM440: { density: 7.85, pricePerKg: 4000, roughRate: 8, finishRate: 20 },
  SUS304: { density: 7.93, pricePerKg: 7000, roughRate: 6, finishRate: 15 },
  AL6061: { density: 2.7, pricePerKg: 8000, roughRate: 30, finishRate: 45 },
  AL7075: { density: 2.8, pricePerKg: 12000, roughRate: 25, finishRate: 40 },
  C1100: { density: 8.9, pricePerKg: 15000, roughRate: 15, finishRate: 30 },
};

export const DEFAULT_STOCK_MARGIN_MM = 3; // 소재 여유(mm, 각 축 양쪽)
export const DEFAULT_SETUP_MINUTES = 30; // 셋업(분, 1회)
export const DEFAULT_HOURLY_RATE_WON = 60000; // 시간당 가공비(원)
export const DEFAULT_MARGIN_RATE = 0.2; // 마진율 20%
export const VAT_RATE = 0.1; // 부가세 10%

/** 원 단위로 반올림(최종 표시용). 계산 중간값은 그대로 두고 맺을 때만 쓴다. */
export const roundWon = (value) => Math.round(Number(value) || 0);

/**
 * bbox(x,y,z, mm) + 소재 여유(mm, 각 축 양쪽으로 margin씩)로 소재 블록 크기를 낸다.
 * 반환은 {x,y,z}(mm) - 화면에서 축별로 따로 수정할 수 있어야 해서 부피가 아니라
 * 치수로 돌려준다.
 */
export const calcStockSize = (bbox, marginMm) => {
  const margin = Number(marginMm) || 0;
  return {
    x: (Number(bbox?.x) || 0) + margin * 2,
    y: (Number(bbox?.y) || 0) + margin * 2,
    z: (Number(bbox?.z) || 0) + margin * 2,
  };
};

/** 소재 블록 치수(mm) → 부피(cm3). */
export const calcStockVolumeCm3 = (stockSize) => {
  const x = Number(stockSize?.x) || 0;
  const y = Number(stockSize?.y) || 0;
  const z = Number(stockSize?.z) || 0;
  return (x * y * z) / 1000;
};

/** 소재비 = 소재 블록 부피(cm3) × 밀도(g/cm3) ÷ 1000(g→kg) × kg단가 × 수량. */
export const calcMaterialCost = ({ stockVolumeCm3, density, pricePerKg, quantity }) => {
  const volume = Number(stockVolumeCm3) || 0;
  const d = Number(density) || 0;
  const price = Number(pricePerKg) || 0;
  const qty = Number(quantity) || 0;
  return (volume * d / 1000) * price * qty;
};

/** 황삭 시간(분) = 제거 부피(cm3) ÷ 재질별 황삭 제거율(cm3/분). */
/** 황삭 제거 부피 = 소재 블록 부피 - 부품 부피(음수면 0). */
export const calcRemovalVolumeCm3 = (stockVolumeCm3, partVolumeCm3) =>
  Math.max(0, (Number(stockVolumeCm3) || 0) - (Number(partVolumeCm3) || 0));

export const calcRoughMinutes = (removalVolumeCm3, roughRate) => {
  const rate = Number(roughRate);
  if (!rate) return 0;
  return (Number(removalVolumeCm3) || 0) / rate;
};

/** 정삭 시간(분) = 표면적(cm2) ÷ 재질별 정삭 속도(cm2/분). */
export const calcFinishMinutes = (areaCm2, finishRate) => {
  const rate = Number(finishRate);
  if (!rate) return 0;
  return (Number(areaCm2) || 0) / rate;
};

/**
 * 가공비 = ((황삭분+정삭분) × 수량 + 셋업분) / 60 × 시간당 가공비.
 * 셋업은 1회(수량과 무관)라 수량을 곱하지 않는다.
 */
export const calcMachiningCost = ({
  roughMinutes,
  finishMinutes,
  quantity,
  setupMinutes = DEFAULT_SETUP_MINUTES,
  hourlyRateWon = DEFAULT_HOURLY_RATE_WON,
}) => {
  const qty = Number(quantity) || 0;
  const perUnitMinutes = (Number(roughMinutes) || 0) + (Number(finishMinutes) || 0);
  const totalMinutes = perUnitMinutes * qty + (Number(setupMinutes) || 0);
  const hours = totalMinutes / 60;
  return hours * (Number(hourlyRateWon) || 0);
};

/** 외주 항목 배열([{name, amount}]) 합계. */
export const calcOutsourceTotal = (items) => {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);
};

/**
 * 소재비+가공비+외주 합(subtotal) → 마진율 적용 공급가 → 부가세 10% → 합계.
 * 공급가 = subtotal × (1 + 마진율) (마진을 "얹는" 마크업 방식).
 */
export const calcQuoteSummary = ({
  materialCost,
  machiningCost,
  outsourceTotal,
  marginRate = DEFAULT_MARGIN_RATE,
}) => {
  const subtotal = (Number(materialCost) || 0) + (Number(machiningCost) || 0) + (Number(outsourceTotal) || 0);
  const supplyPrice = subtotal * (1 + (Number(marginRate) || 0));
  const vat = supplyPrice * VAT_RATE;
  const total = supplyPrice + vat;
  return {
    subtotal: roundWon(subtotal),
    supplyPrice: roundWon(supplyPrice),
    vat: roundWon(vat),
    total: roundWon(total),
  };
};

/**
 * 부품 1종(조립품이면 부품 여러 종 중 하나) 원가 한 줄.
 * - 구매품: 단가 × 수량만(소재비/가공비 없음) - 외주/구매 부품은 가공을 안 하니까.
 * - 가공품: 기존 단일 부품 로직(소재비 + 가공비)을 그대로 재사용 - 부품마다 자기
 *   bbox/재질/소재여유로 소재 블록을 따로 낸다.
 * part: { quantity, volumeCm3, areaCm2, stockSize, purchased, purchasedUnitPrice }
 * materialInputs: { density, pricePerKg, roughRate, finishRate }
 * machiningInputs: { setupMinutes, hourlyRateWon }
 */
export const calcPartCost = (part, materialInputs, machiningInputs = {}) => {
  const quantity = Number(part?.quantity) || 0;

  if (part?.purchased) {
    const unitPrice = Number(part?.purchasedUnitPrice) || 0;
    const purchaseCost = unitPrice * quantity;
    return {
      quantity,
      stockVolumeCm3: 0,
      removalVolumeCm3: 0,
      materialCost: 0,
      roughMinutes: 0,
      finishMinutes: 0,
      machiningCost: 0,
      purchaseCost,
      subtotal: purchaseCost,
    };
  }

  const stockVolumeCm3 = calcStockVolumeCm3(part?.stockSize);
  const materialCost = calcMaterialCost({
    stockVolumeCm3,
    density: materialInputs?.density,
    pricePerKg: materialInputs?.pricePerKg,
    quantity,
  });
  const removalVolumeCm3 = calcRemovalVolumeCm3(stockVolumeCm3, part?.volumeCm3);
  const roughMinutes = calcRoughMinutes(removalVolumeCm3, materialInputs?.roughRate);
  const finishMinutes = calcFinishMinutes(part?.areaCm2, materialInputs?.finishRate);
  const machiningCost = calcMachiningCost({
    roughMinutes,
    finishMinutes,
    quantity,
    setupMinutes: machiningInputs?.setupMinutes,
    hourlyRateWon: machiningInputs?.hourlyRateWon,
  });
  const purchaseCost = 0;
  return {
    quantity,
    stockVolumeCm3,
    removalVolumeCm3,
    materialCost,
    roughMinutes,
    finishMinutes,
    machiningCost,
    purchaseCost,
    subtotal: materialCost + machiningCost,
  };
};

/**
 * 저장용으로 분석 결과에서 mesh/previewSvg를 뺀다(요구사항: "용량이 커서 저장 안 함").
 * 3D 돌려보기/6면도는 화면에서만 쓰고 DB에는 안 들고 가도 되는 값이라 - 저장된 견적을
 * 다시 열 때는 치수/표/계산값만 보여주면 된다(6면도는 그때그때 재생성하지 않고 그냥 생략).
 * 얕은 복사가 아니라 각 필드를 새로 만들어 돌려준다 - 원본 analysis 객체(화면 상태)를
 * 건드리지 않는다.
 */
export const stripAnalysisForSave = (analysis) => {
  if (!analysis) return analysis;
  const { mesh, previewSvg, parts, ...rest } = analysis;
  return {
    ...rest,
    parts: (parts || []).map(({ previewSvg: partPreviewSvg, ...part }) => part),
  };
};

/**
 * 전체(완성품) 기준 형상 특징 - "비슷한 과거 견적 찾기"(POST /quotes/similar) 요청에
 * 그대로 넣는다. 레이어로 나눈 뒤라면 form.layered가 true이고 form.parts가 레이어들이라
 * layerCnt를 그 수로 잡는다(안 나눴으면 0 - 요구사항: "after a layer split, use the
 * split result's partCnt/layerCnt").
 */
export const featuresFromAnalysis = (analysis, form) => {
  const parts = (form && form.parts) || (analysis && analysis.parts) || [];
  const partCnt = parts.length || 1;
  const layerCnt = form && form.layered ? partCnt : 0;
  return {
    bbox: {
      x: Number(analysis?.bbox?.x) || 0,
      y: Number(analysis?.bbox?.y) || 0,
      z: Number(analysis?.bbox?.z) || 0,
    },
    volumeCm3: Number(analysis?.volumeCm3) || 0,
    areaCm2: Number(analysis?.areaCm2) || 0,
    partCnt,
    layerCnt,
  };
};

/**
 * 레이어로 나누기 패널에서 "나눠서 다시 분석"을 누르기 전에, 고른 분할 레벨로
 * 자르면 두께가 어떻게 되는지 미리 보여주려는 순수 계산. 실제 분할(불린 연산)은
 * 서버(cad-service)가 하지만, 두께 미리보기는 min/max/레벨만 있으면 바로 알 수
 * 있어 요청을 보내기 전에 화면에서 바로 계산한다.
 * 반환은 위(큰 값)에서 아래 순서의 [{top, bottom, thickness}] - geometry.slice_solid와 같은 순서.
 */
export const calcLayerThicknesses = (levels, min, max) => {
  const lo = Number(min);
  const hi = Number(max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return [];
  const uniqueLevels = Array.from(new Set((levels || []).map(Number).filter(Number.isFinite)));
  const bounds = Array.from(new Set([lo, ...uniqueLevels, hi])).sort((a, b) => a - b);
  const slabs = [];
  for (let i = 1; i < bounds.length; i += 1) {
    const bottom = bounds[i - 1];
    const top = bounds[i];
    if (top - bottom < 1e-6) continue;
    slabs.push({ top, bottom, thickness: top - bottom });
  }
  return slabs.reverse();
};

/**
 * 조립비(선택) = 분 × 시간당 가공비. 수량과 무관하게 조립 전체에 1번(셋업처럼).
 */
export const calcAssemblyLaborCost = (assemblyMinutes, hourlyRateWon = DEFAULT_HOURLY_RATE_WON) => {
  const minutes = Number(assemblyMinutes) || 0;
  return (minutes / 60) * (Number(hourlyRateWon) || 0);
};

/**
 * 부품별 원가 라인을 합쳐 조립품(또는 단일 부품) 전체 합계를 낸다.
 * parts: calcPartCost에 넣을 part 객체 배열, partMaterials: part.id -> materialInputs,
 * outsourceItems: 기존 외주 항목, assemblyMinutes: 선택적 조립비(분).
 */
export const calcAssemblyTotals = ({
  parts,
  partMaterials,
  machiningInputs,
  outsourceItems,
  assemblyMinutes = 0,
  marginRate = DEFAULT_MARGIN_RATE,
}) => {
  const lines = (parts || []).map((part) => ({
    partId: part.id,
    // 셋업은 부품마다 따로다(부품이 바뀌면 기계에 다시 물려야 하니까) - part.setupMinutes가
    // 있으면 그걸 쓰고, 없으면 공용 기본값(machiningInputs.setupMinutes)으로.
    ...calcPartCost(part, partMaterials?.[part.id] || {}, {
      ...machiningInputs,
      setupMinutes: part.setupMinutes ?? machiningInputs?.setupMinutes,
    }),
  }));
  const materialCost = lines.reduce((sum, l) => sum + l.materialCost, 0);
  const machiningCost = lines.reduce((sum, l) => sum + l.machiningCost, 0);
  const purchaseCost = lines.reduce((sum, l) => sum + l.purchaseCost, 0);
  const assemblyLaborCost = calcAssemblyLaborCost(assemblyMinutes, machiningInputs?.hourlyRateWon);
  const outsourceTotal = calcOutsourceTotal(outsourceItems);
  const summary = calcQuoteSummary({
    materialCost,
    machiningCost: machiningCost + assemblyLaborCost,
    outsourceTotal: outsourceTotal + purchaseCost,
    marginRate,
  });
  return { lines, materialCost, machiningCost, purchaseCost, assemblyLaborCost, outsourceTotal, ...summary };
};
